import { generateNotAuthorizedError, isNotAuthorizedApiError } from "@/auth";
import { auth } from "@/auth/auth";
import { openSigninDialog } from "@/auth/components/SigninDialog";
import { toIResponseError } from "@/model/error";
import { DATA_PLATFORM_API_URL } from "@/utils/config";
import { toast } from "@deputy-ai/ui/toast/Toast";
import { createClient, type Options } from "@hey-api/client-fetch";
import { Observable } from "rxjs";

export function getDataPlatformApiEndpoint() {
  if (typeof window === "undefined" || process.env.STORYBOOK) {
    return DATA_PLATFORM_API_URL;
  }

  return "/api";
}

type ApiRequest<Req, Resp> = (request: Options<Req>) => Promise<Resp>;

export function patchClient<Req, Resp, Result = Resp>(
  fn: ApiRequest<Req, Resp>,
  arg?: boolean | { authed?: boolean; mapResp?: (resp: Resp) => Result }
): ApiRequest<Req, Result> {
  const options = typeof arg === "boolean" ? { authed: arg } : arg;

  const { authed = true, mapResp } = options ?? {};

  return async (request: Options<Req>) => {
    const client =
      authed && (typeof window === "undefined" || process.env.STORYBOOK) // only authed client in server side
        ? await createAuthedClient()
        : await createNonAuthedClient();
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    request.client ||= client;

    if (process.env.NEXT_PUBLIC_API_DELAY_MS) {
      // eslint-disable-next-line promise/avoid-new
      await new Promise((resolve) => {
        setTimeout(
          resolve,
          Number.parseInt(process.env.NEXT_PUBLIC_API_DELAY_MS!)
        );
      });
    }
    const resp = await fn(request);

    const notAuthorizedHandler = await handleIfNotAuthorized(resp);
    if (notAuthorizedHandler) {
      return notAuthorizedHandler;
    }

    if (mapResp) {
      return mapResp(resp);
    }

    return resp as any;
  };
}

export function mapRespData<Resp extends { data?: any }, R>(
  resp: Resp,
  fn: (data: Resp["data"]) => R
): Omit<Resp, "data"> & { data: R } {
  if ("data" in resp) {
    return {
      ...resp,
      data: fn(resp.data),
    };
  }

  return resp as any;
}

export async function resolvePromise<T>(
  promise: Promise<T>
): Promise<{ data: T } | { error: unknown }> {
  return await promise.then(
    (data) => ({ data }),
    (error: unknown) => ({ error })
  );
}

export interface ServerActionResult<T> {
  data?: T;
  error?: unknown;
}

export function toServerActionResult<T = any>(resp: {
  data?: T;
  error?: unknown;
}): ServerActionResult<T> {
  return {
    data: resp.data,
    error: resp.error,
  };
}

export function getErrorMessage(error: any, defaultMessage?: string): string {
  return (
    ((error?.message as string) || undefined) ??
    defaultMessage ??
    "Unknown error"
  );
}

export async function createAuthedClient() {
  const accessToken = await (async () => {
    if (process.env.STORYBOOK) {
      return window.localStorage.getItem("api_access_token") ?? "";
    }
    const session = await auth();

    return session?.authToken.accessToken ?? "";
  })();
  return createClient({
    baseUrl: getDataPlatformApiEndpoint(),
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export async function createNonAuthedClient() {
  return createClient({
    baseUrl: getDataPlatformApiEndpoint(),
  });
}

export function callApiObservable<T>(
  callApi: (args: {
    controller: AbortController;
  }) => Promise<ServerActionResult<T>>,
  options?: {
    toastError?: boolean;
  }
) {
  return new Observable<T>((subscriber) => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const resp = await callApi({
          controller,
        });

        if (resp.error) {
          subscriber.error(resp.error);
          if (options?.toastError) {
            toast({
              title: "Error",
              variant: "destructive",
              description: toIResponseError(resp.error).detail,
            });
          }
          return;
        }

        subscriber.next(resp.data as T);
        subscriber.complete();
      } catch (error) {
        subscriber.error(error);
      }
    };

    void load();

    return () => {
      controller.abort("cancel");
    };
  });
}

async function handleIfNotAuthorized(resp: any) {
  if (isNotAuthorizedApiError(resp?.error)) {
    if (typeof window !== "undefined") {
      openSigninDialog();

      // eslint-disable-next-line promise/avoid-new
      return await new Promise<any>(() => {
        // never resolve
      });
    }

    throw generateNotAuthorizedError(resp.error);
  }
}
